<script setup>
import { ref, computed, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useNavigator } from '@/composables/navigator'
import { useDetectDevice } from '@/composables/device'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import CollapsibleDetailHeader from '@/components/shared/CollapsibleDetailHeader.vue'
import DetailField from '@/components/shared/DetailField.vue'
import ContactReferencesDetail from '@/components/shared/ContactReferencesDetail.vue'
import ContactEdit from '@/components/shared/ContactEdit.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
})

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()

const contact = ref(props.modelValue)
const editItem = ref(null)

let changed = false
const emit = defineEmits(['cancel', 'update'])
const onNavigateBack = () => (changed ? emit('update') : emit('cancel'))
useNavigator(onNavigateBack)

const fullName = computed(() => planningStore.getContactName(contact.value))

const reload = async() => {
  contact.value = (await planningStore.fetchContactByIdAsync(contact.value.id)).data
}

onMounted(async() => {
  if (contact.value?.id) await reload()
})

const onEdited = async() => {
  editItem.value = null
  changed = true
  await reload()
}
</script>

<template>
  <q-card v-if="contact" class="dialog-card-flex">
    <q-dialog
      :model-value="editItem !== null"
      :maximized="isMobile"
      class="dialog-xs"
      @hide="editItem = null"
    >
      <contact-edit
        v-if="editItem !== null"
        :model-value="editItem"
        @update="onEdited"
        @cancel="editItem = null"
      />
    </q-dialog>
    <q-card-section class="scroll col q-pa-none dialog-card-content-scroll">
      <navigate-back
        :text="fullName"
        :archived="contact?.archived"
        @cancel="onNavigateBack"
      >
        <template #right>
          <div style="display: flex; align-items: center;">
            <q-btn
              flat
              round
              icon="mdi-pencil"
              @click="editItem = { ...contact }"
            />
          </div>
        </template>
      </navigate-back>
      <collapsible-detail-header
        v-model:compact="webSiteStore.contactDetailHeaderCompact"
        :avatar="contact.avatar_file || contact.avatar_url"
        avatar-icon="mdi-account"
      >
        <template #chips>
          <template v-if="webSiteStore.contactDetailHeaderCompact">
            <div class="column items-end" style="gap: 0.25rem;">
              <q-chip
                v-if="contact.role"
                dense
                size="md"
                color="blue-grey"
                text-color="white"
                icon="mdi-badge-account"
                :label="contact.role"
              />
              <q-chip
                v-if="contact.is_default"
                dense
                size="md"
                color="primary"
                text-color="white"
                :label="i18n.t('company.defaultContact')"
              />
            </div>
          </template>
          <template v-else>
            <q-chip
              v-if="contact.is_default"
              dense
              size="md"
              color="primary"
              text-color="white"
              :label="i18n.t('company.defaultContact')"
            />
          </template>
        </template>
        <template #info>
          <q-list class="q-px-sm">
            <q-item>
              <q-item-section>
                <div class="row items-start" style="column-gap: 1rem;">
                  <detail-field class="col" :label="i18n.t('common.role')" :value="contact.role" />
                  <detail-field class="col" right :label="i18n.t('company.defaultContact')">
                    <q-icon
                      :name="contact.is_default ? 'mdi-check-circle' : 'mdi-minus'"
                      :color="contact.is_default ? 'positive' : 'grey-5'"
                    />
                  </detail-field>
                </div>
              </q-item-section>
            </q-item>
          </q-list>
        </template>
      </collapsible-detail-header>
      <contact-references-detail :contact="contact" />
    </q-card-section>
  </q-card>
</template>
